import { LoadingLayer } from '../../LoadingLayer';
import { useEffect, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { SideMenuAdmin } from './SideMenuAdmin';
import '../acceptance-letters/AcceptanceLetter.css';
import './Admin.css';
import Background from '../../../assets/participation-certificate/background.jpeg';
import Signature from '../../../assets/participation-certificate/signature.png';
import Firma from '../../../assets/participation-certificate/firma.png';

const API_BASE = 'https://nt6dwl1xpc.execute-api.us-west-1.amazonaws.com/produccion';
const ATTENDANCE_GROUP = 'attendance-certificate';

const fetchUsers = async (setUsers) => {
  try {
    const response = await fetch(`${API_BASE}/admin/coordinators`, {
      headers: { 'user-token': localStorage.getItem('id_token') },
    });
    const data = await response.json();
    setUsers(data.users || []);
  } catch (error) {
    console.error('Error fetching users:', error);
  }
};

const grantAttendanceCertificate = async ({ username, email, name }) => {
  const response = await fetch(`${API_BASE}/admin/attendance-certificate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user-token': localStorage.getItem('id_token'),
    },
    body: JSON.stringify({ username, email, name }),
  });
  if (!response.ok) throw new Error('Request failed');
  return response.json();
};

const revokeAttendanceCertificate = async ({ username }) => {
  const response = await fetch(`${API_BASE}/admin/coordinators/manage-group`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user-token': localStorage.getItem('id_token'),
    },
    body: JSON.stringify({
      username,
      group_name: ATTENDANCE_GROUP,
      action: 'remove',
    }),
  });
  if (!response.ok) throw new Error('Request failed');
  return response.json();
};

const STAFF_GROUPS = new Set([
  'admin',
  'coordinator',
  'revisor',
  'revisor-advanced-materials-and-physics-of-nanostructures',
  'revisor-bionanotechnology',
  'revisor-nanophotonics',
  'revisor-physical-chemistry-of-nanomaterials-and-nanocatalysis',
  'revisor-tech-inovation-in-science',
]);

export const AdminAttendanceCertificates = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideStaff, setHideStaff] = useState(true);
  const [updatingUsers, setUpdatingUsers] = useState({});
  const [previewUser, setPreviewUser] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const sanitizeFilename = (value) =>
    (value || 'certificate')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z0-9-_ ]/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .slice(0, 80) || 'certificate';

  const downloadAttendancePdf = async () => {
    const element = document.getElementById('admin-attendance-printable');
    if (!element || isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `SNN2026_Attendance_${sanitizeFilename(previewUser.name || previewUser.email)}.pdf`;
      await html2pdf()
        .from(element)
        .set({
          margin: 0,
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
          },
          jsPDF: { unit: 'in', format: [11, 8.5], orientation: 'landscape' },
        })
        .save();
    } catch (error) {
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    setLoadingMessage('Loading users...');
    fetchUsers(setUsers).then(() => setIsLoading(false));
  }, []);

  const hasCertificate = (user) => (user.groups || []).includes(ATTENDANCE_GROUP);

  const handleSend = async (user) => {
    setUpdatingUsers((prev) => ({ ...prev, [user.username]: 'sending' }));
    try {
      await grantAttendanceCertificate({
        username: user.username,
        email: user.email,
        name: user.name,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.username === user.username
            ? { ...u, groups: [...new Set([...(u.groups || []), ATTENDANCE_GROUP])] }
            : u,
        ),
      );
    } catch (error) {
      alert('Error sending attendance certificate');
    } finally {
      setUpdatingUsers((prev) => ({ ...prev, [user.username]: null }));
    }
  };

  const handleRevoke = async (user) => {
    const label = user.name ? `${user.name} (${user.email})` : user.email;
    if (!window.confirm(`Revoke attendance certificate for ${label}?`)) return;
    setUpdatingUsers((prev) => ({ ...prev, [user.username]: 'revoking' }));
    try {
      await revokeAttendanceCertificate({ username: user.username });
      setUsers((prev) =>
        prev.map((u) =>
          u.username === user.username
            ? { ...u, groups: (u.groups || []).filter((g) => g !== ATTENDANCE_GROUP) }
            : u,
        ),
      );
    } catch (error) {
      alert('Error revoking attendance certificate');
    } finally {
      setUpdatingUsers((prev) => ({ ...prev, [user.username]: null }));
    }
  };

  const filteredUsers = users
    .filter((u) => {
      if (hideStaff && (u.groups || []).some((g) => STAFF_GROUPS.has(g))) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      return (
        (u.email || '').toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an && bn) return an.localeCompare(bn);
      if (an && !bn) return -1;
      if (!an && bn) return 1;
      return (a.email || '').localeCompare(b.email || '');
    });

  const hasRealName = (u) => {
    const n = (u.name || '').trim();
    if (!n) return false;
    if (/\d{5,}/.test(n)) return false; // Google-default IDs like "user 117234234..."
    if (/^[\d\s._-]+$/.test(n)) return false; // purely numeric / punctuation
    return true;
  };
  const namedCount = users.filter(hasRealName).length;

  const grantedCount = users.filter(hasCertificate).length;

  return (
    <div className="snn-user-home-main-wrapper">
      <SideMenuAdmin />
      <div className="snn-abstract-submission-main-grid-wrapper">
        <LoadingLayer isLoading={isLoading} action={loadingMessage} />

        <section className="snn-expand-grid-4">
          <h1 className="snn-user-home-main-content-header">Attendance certificates</h1>

          <p style={{ fontSize: '14px', color: '#475569', marginBottom: '16px' }}>
            Issue an SNN 2026 attendance certificate to participants who did not present an
            abstract. The user will be added to the <code>{ATTENDANCE_GROUP}</code> group and an
            email notification will be sent.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
              <span
                className="material-symbols-outlined"
                style={{
                  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  color: '#94a3b8', fontSize: '20px',
                }}
              >
                search
              </span>
              <input
                type="text"
                placeholder="Search by email, username or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px 10px 36px', fontSize: '14px',
                  border: '1px solid #ddd', borderRadius: '6px', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#94a3b8', fontSize: '18px', lineHeight: 1, padding: 0,
                  }}
                >×</button>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#334155' }}>
              <input
                type="checkbox"
                checked={hideStaff}
                onChange={(e) => setHideStaff(e.target.checked)}
              />
              Hide staff (admins, coordinators, revisors)
            </label>
            <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
              {filteredUsers.length} / {users.length} users · {grantedCount} granted · {namedCount} with name
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>User</th>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Status</th>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Certificate</th>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const granted = hasCertificate(user);
                  const updating = updatingUsers[user.username];
                  return (
                    <tr key={user.username} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px 12px' }}>
                        {user.name ? (
                          <>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{user.name}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{user.email}</div>
                          </>
                        ) : (
                          <>
                            <div style={{ color: '#0f172a' }}>{user.email}</div>
                            <div style={{ fontSize: '11px', color: '#dc2626', fontStyle: 'italic' }}>
                              No name in Cognito
                            </div>
                          </>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span
                          style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
                            fontSize: '11px', fontWeight: 600,
                            backgroundColor: user.enabled ? '#dcfce7' : '#fef3c7',
                            color: user.enabled ? '#166534' : '#92400e',
                          }}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {granted ? (
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: '12px',
                            fontSize: '12px', fontWeight: 700,
                            backgroundColor: '#dcfce7', color: '#166534',
                          }}>
                            ✓ Sent
                          </span>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>Not sent</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => handleSend(user)}
                            disabled={updating === 'sending'}
                            style={{
                              padding: '4px 12px', fontSize: '12px',
                              backgroundColor: granted ? '#f59e0b' : '#0b6ef6',
                              color: '#fff', border: 'none', borderRadius: '4px',
                              cursor: updating === 'sending' ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {updating === 'sending'
                              ? 'Sending…'
                              : granted ? 'Resend' : 'Send certificate'}
                          </button>
                          {granted && (
                            <>
                              <button
                                onClick={() => setPreviewUser(user)}
                                style={{
                                  padding: '4px 12px', fontSize: '12px',
                                  backgroundColor: '#166534', color: '#fff',
                                  border: 'none', borderRadius: '4px', cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: 2 }}>download</span>
                                PDF
                              </button>
                              <button
                                onClick={() => handleRevoke(user)}
                                disabled={updating === 'revoking'}
                                style={{
                                  padding: '4px 10px', fontSize: '12px',
                                  backgroundColor: 'transparent',
                                  color: '#dc2626', border: '1px solid #dc2626',
                                  borderRadius: '4px',
                                  cursor: updating === 'revoking' ? 'not-allowed' : 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {updating === 'revoking' ? '…' : 'Revoke'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {previewUser && (
            <div
              style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', zIndex: 1000,
              }}
              onClick={() => setPreviewUser(null)}
            >
              <div
                style={{
                  backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
                  maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}
                onClick={e => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ margin: 0, fontSize: '18px', color: '#222' }}>Attendance Certificate</h2>
                  <button
                    onClick={() => setPreviewUser(null)}
                    style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666', padding: '0 4px', lineHeight: 1 }}
                  >×</button>
                </div>
                <button className="print-button" onClick={downloadAttendancePdf} disabled={isGeneratingPdf}>
                  <span className="material-symbols-outlined">download</span>
                  {isGeneratingPdf ? 'Generating PDF…' : 'Download PDF'}
                </button>
                <div id="admin-attendance-printable">
                  <div className="participation-cert-page">
                    <img src={Background} className="participation-cert-bg" alt="" />
                    <div className="participation-cert-content">
                      <h1 className="participation-cert-title">
                        The Organizing Committee of SNN 2026 certifies that:
                      </h1>
                      <p className="participation-cert-author">{previewUser.name || previewUser.email}</p>
                      <p className="participation-cert-subtitle">
                        Attended the{' '}
                        <span className="participation-cert-type">
                          Symposium of Nanoscience and Nanomaterials 2026
                        </span>
                        , held in Ensenada, B. C. Mexico from May 11 to 15, 2026.
                      </p>
                    </div>
                    <div className="participation-cert-footer">
                      <img src={Firma} className="participation-cert-signature" alt="Signature" />
                      <img src={Signature} className="participation-cert-signature" alt="Signature" />
                      <div className="participation-cert-signature-line">
                        ________________________________
                      </div>
                      <p className="participation-cert-chair">Dr. Uriel Caudillo Flores</p>
                      <p className="participation-cert-chair-role">SNN 2026 Chairman</p>
                      <p className="participation-cert-chair-date">
                        May 11 to 15, 2026 // Ensenada, B. C. Mexico
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
